import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Copy, Eye, EyeOff, RefreshCw, ShieldAlert, Check } from "lucide-react";

/** Admin-only credential viewer for a single IBA.
 *  Shows plaintext (if captured post-Feb-2026) with reveal + copy, and a
 *  one-click "Reset & Regenerate" that mints a fresh strong password. All
 *  reads and resets are audit-logged server-side.
 */
export default function AdminUserPasswordCard({ userCode }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/admin/users/${userCode}/password`)
      .then((r) => setInfo(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load password"))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (userCode) load(); }, [userCode]);

  const doReset = async () => {
    if (!window.confirm(`Reset password for ${userCode}?\nThe new password will be generated automatically, shown once on this screen, and will invalidate the old one immediately.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${userCode}/reset-password`);
      setInfo({
        user_code: data.user_code,
        email: data.email,
        password_plain: data.password_plain,
        password_updated_at: data.password_updated_at,
        reason: null,
      });
      setReveal(true);
      toast.success(`Password reset for ${data.user_code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = (value, label) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };

  return (
    <section
      data-testid="admin-user-password-card"
      className="bg-white border border-border rounded-sm overflow-hidden"
    >
      <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <KeyRound size={14} className="text-gold-deep" />
          <h3 className="font-serif text-base text-navy">Login credentials</h3>
        </div>
        <button
          type="button"
          onClick={doReset}
          disabled={busy}
          data-testid="admin-reset-password-btn"
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-3 py-1.5 bg-royal text-white hover:bg-royal-deep rounded-sm disabled:opacity-50"
        >
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} />
          {busy ? "Resetting…" : "Reset & Regenerate"}
        </button>
      </div>

      <div className="p-5 space-y-3 text-sm">
        {loading ? (
          <div className="text-navy/50 text-xs">Loading…</div>
        ) : info?.password_plain ? (
          <>
            <Row label="Email" value={info.email} onCopy={() => copy(info.email, "Email")} />
            <div className="flex items-center gap-2 bg-cream/40 border border-border rounded-sm px-3 py-2.5" data-testid="admin-password-row">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest text-navy/50">Password</div>
                <div className="font-mono text-navy text-sm truncate mt-0.5" data-testid="admin-password-value">
                  {reveal ? info.password_plain : "•".repeat(Math.min(info.password_plain.length, 14))}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setReveal((v) => !v)}
                title={reveal ? "Hide" : "Reveal"}
                data-testid="admin-password-toggle"
                className="p-1.5 text-navy/60 hover:text-royal hover:bg-royal/5 rounded"
              >
                {reveal ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
              <button
                type="button"
                onClick={() => copy(info.password_plain, "Password")}
                title="Copy password"
                data-testid="admin-password-copy"
                className="p-1.5 text-navy/60 hover:text-royal hover:bg-royal/5 rounded"
              >
                <Copy size={13} />
              </button>
            </div>
            {info.password_updated_at && (
              <div className="text-[11px] text-navy/50">
                Last updated {new Date(info.password_updated_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </div>
            )}
          </>
        ) : (
          <div className="flex items-start gap-2 bg-gold/5 border border-gold/25 rounded-sm px-3 py-3" data-testid="admin-password-legacy">
            <ShieldAlert size={14} className="text-gold-deep shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="text-navy font-medium">Legacy account — password is not visible</div>
              <p className="text-[12px] text-navy/60 mt-0.5">
                This user&apos;s password was set before plaintext capture was enabled. Click <b>Reset &amp; Regenerate</b> above to mint a fresh password that will be visible here going forward.
              </p>
            </div>
          </div>
        )}
        <div className="text-[10.5px] text-navy/50 flex items-center gap-1.5 pt-1 border-t border-line">
          <Check size={11} className="text-olive" /> Every reveal and reset is written to <code>audit_log</code>.
        </div>
      </div>
    </section>
  );
}

function Row({ label, value, onCopy }) {
  return (
    <div className="flex items-center gap-2 bg-white border border-border rounded-sm px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-navy/50">{label}</div>
        <div className="text-navy text-sm truncate mt-0.5">{value || "—"}</div>
      </div>
      <button type="button" onClick={onCopy} title={`Copy ${label}`} className="p-1.5 text-navy/60 hover:text-royal hover:bg-royal/5 rounded">
        <Copy size={13} />
      </button>
    </div>
  );
}
